/**
 * Polish UI strings for the Contact Request epic.
 *
 * ## Organisation
 * Keys are grouped by the feature or screen they belong to:
 *
 * | Prefix            | Used in                        |
 * | ----------------- | ------------------------------ |
 * | `VIEW_CR_*`       | `ViewContactRequest` page      |
 * | `UPDATE_STATUS_*` | `UpdateStatusModal` component  |
 *
 * ## Usage
 * Import the re-exported `CONTACT_REQUEST_LABELS` from `../constants/labels`
 * instead of importing this file directly.
 */
const pl = {
  // ─── ViewContactRequest page ──────────────────────────────────────────────

  /** Page title shown in `InlineLayout`. */
  VIEW_CR_TITLE: 'Podgląd zgłoszenia kontaktowego',

  /** Label for the header button that opens the status-update modal. */
  VIEW_CR_BUTTON_UPDATE_STATUS: 'Zmiana statusu',

  /** Full-page error message shown when the API call fails or returns no data. */
  VIEW_CR_ERROR_LOAD: 'Nie udało się wczytać zgłoszenia. Spróbuj ponownie.',

  /** Success snackbar message shown after a status transition completes. */
  VIEW_CR_SUCCESS_STATUS_UPDATED: 'Status został pomyślnie zaktualizowany.',

  // Navigation tabs

  /** Label for the "basic information" navigation tab. */
  VIEW_CR_NAV_BASIC_INFO: 'Podstawowe informacje',

  /** Label for the "admin notes" navigation tab. */
  VIEW_CR_NAV_ADMIN_NOTES: 'Notatki administratora',

  // Section headings

  /** Heading for the client details card. */
  VIEW_CR_SECTION_BASIC_DATA: 'Podstawowe dane',

  /** Heading for the client address card. */
  VIEW_CR_SECTION_ADDRESS: 'Dane adresowe',

  /** Heading for the admin notes card. */
  VIEW_CR_SECTION_ADMIN_NOTES: 'Notatki administratora',

  // Field labels — basic info

  /** Field label for the sequential client identifier (e.g. CLIENT000001). */
  VIEW_CR_FIELD_CLIENT_ID: 'ID klienta',

  /** Field label for the client's display name. */
  VIEW_CR_FIELD_CLIENT_NAME: 'Nazwa klienta',

  /** Field label for the client's email address. */
  VIEW_CR_FIELD_EMAIL: 'Adres e-mail',

  /** Field label for the client's phone number. */
  VIEW_CR_FIELD_PHONE: 'Numer telefonu',

  /** Field label for the ISO-8601 creation timestamp, formatted for display. */
  VIEW_CR_FIELD_CREATED_AT: 'Data utworzenia',

  /** Field label for the contact request subject line. */
  VIEW_CR_FIELD_SUBJECT: 'Temat',

  /** Field label for the contact request message body. */
  VIEW_CR_FIELD_MESSAGE: 'Wiadomość',

  // Field labels — address

  /** Field label for the first line of the client's postal address. */
  VIEW_CR_FIELD_ADDRESS_LINE1: 'Adres (linia 1)',

  /** Field label for the optional second line of the client's postal address. */
  VIEW_CR_FIELD_ADDRESS_LINE2: 'Adres (linia 2)',

  /** Field label for the client's city. */
  VIEW_CR_FIELD_CITY: 'Miejscowość',

  /** Field label for the client's province / voivodeship. */
  VIEW_CR_FIELD_STATE: 'Województwo',

  /** Field label for the client's postal code. */
  VIEW_CR_FIELD_ZIP: 'Kod pocztowy',

  /** Field label for the client's country. */
  VIEW_CR_FIELD_COUNTRY: 'Kraj',

  // Admin notes tab

  /** Empty-state message shown when the request has no admin notes yet. */
  VIEW_CR_NOTES_EMPTY: 'Administrator nie dodał jeszcze żadnych notatek.',

  // ─── UpdateStatusModal ────────────────────────────────────────────────────

  /** Dialog title rendered at the top of the status-update modal. */
  UPDATE_STATUS_MODAL_TITLE: 'Zmiana statusu zgłoszenia kontaktowego',

  /** Label for the read-only "current status" field. */
  UPDATE_STATUS_FIELD_CURRENT: 'Aktualny status',

  /** Label / floating placeholder for the "new status" dropdown. */
  UPDATE_STATUS_FIELD_NEW: 'Nowy status',

  /** Label for the optional admin note textarea. */
  UPDATE_STATUS_FIELD_NOTE: 'Notatka administratora',

  /** Parenthetical suffix appended to the note label to mark it optional. */
  UPDATE_STATUS_FIELD_NOTE_OPTIONAL: '(Opcjonalne)',

  /** Placeholder text inside the note textarea. */
  UPDATE_STATUS_PLACEHOLDER_NOTE: 'Notatka administratora',

  /** Bold portion of the irreversibility warning banner. */
  UPDATE_STATUS_BANNER_BOLD: 'Zmiana statusu jest nieodwracalna.',

  /** Non-bold continuation of the irreversibility warning banner. */
  UPDATE_STATUS_BANNER_REST:
    ' Po zaktualizowaniu nie będzie można przywrócić poprzedniego statusu.',

  /** Disabled-item label shown in the dropdown when no transitions are available. */
  UPDATE_STATUS_EMPTY_OPTIONS: 'Brak dostępnych statusów',

  /** Label for the cancel button. */
  UPDATE_STATUS_BUTTON_CANCEL: 'Anuluj',

  /** Label for the submit button in its idle state. */
  UPDATE_STATUS_BUTTON_SUBMIT: 'Zmiana statusu',

  /** Label for the submit button while the API request is in-flight. */
  UPDATE_STATUS_BUTTON_SUBMITTING: 'Aktualizowanie statusu…',

  /**
   * Label for the submit button in step 1 of the cancellation flow.
   * Replaces the normal submit label when "Cancelled" is selected.
   */
  UPDATE_STATUS_BUTTON_CONTINUE: 'Continue',

  // ─── CancelContactRequestModal ───────────────────────────────────────────

  /** Dialog title for the cancellation confirmation modal. */
  CANCEL_CR_MODAL_TITLE: 'Confirm Cancellation',

  /** Subtitle / description shown below the title. */
  CANCEL_CR_MODAL_DESC:
    'Proszę podać powód anulowania. Informacja zostanie przesłana do klienta e-mailem.',

  /** Heading for the read-only client-details section. */
  CANCEL_CR_SECTION_CLIENT_DATA: 'Dane klienta',

  /** Field label for the client name row in the client-details section. */
  CANCEL_CR_FIELD_CLIENT_NAME: 'Nazwa klienta',

  /** Field label for the client email row in the client-details section. */
  CANCEL_CR_FIELD_CLIENT_EMAIL: 'Adres e-mail',

  /** Floating label for the cancellation reason textarea. */
  CANCEL_CR_REASON_LABEL: 'Enter a Reason for Cancellation',

  /** Placeholder text inside the cancellation reason textarea. */
  CANCEL_CR_REASON_PLACEHOLDER: 'Wpisz powód anulowania…',

  /** Inline validation error when the reason is left blank. */
  CANCEL_CR_REASON_ERROR: 'To pole jest wymagane',

  /** Label for the "go back" button in the cancellation confirmation modal. */
  CANCEL_CR_BUTTON_BACK: 'Wstecz',

  /** Label for the confirm-cancel submit button in its idle state. */
  CANCEL_CR_BUTTON_SUBMIT: 'Cancel Request',

  /** Label for the confirm-cancel submit button while the API request is in-flight. */
  CANCEL_CR_BUTTON_SUBMITTING: 'Cancelling Request...',

  // ─── ViewContactRequest — cancellation-specific snackbar ─────────────────

  /** Success snackbar message shown after a contact request is cancelled. */
  VIEW_CR_SUCCESS_CANCELLED: 'Contact request has been cancelled successfully.',
} as const;

export default pl;
